/* eslint-disable no-nested-ternary */
import React, { Fragment, useCallback, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Flex } from "../../components/Flex";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { userSliceSelectors } from "../../redux/user/user.selector";
import { SubHeader } from "../../components/SubHeader";
import { SearchDto } from "../../model/search/dto/SearchDto";
import { appSliceSelectors } from "../../redux/app/app.selector";
import { appActions } from "../../redux/app/app.slice";
import debounce from "lodash/debounce";
import { Input } from "../../components/Input";
import { FontSize, Text } from "../../components/Text";
import { SearchCardEntry } from "./components/SearchCardEntry";
import { SearchCardKanbanCard } from "./components/SearchCardKanbanCard";
import { AtiraEmpty } from "../../components/AtiraEmpty";
import { SearchResponseDto } from "../../model/search/dto/SearchResponseDto";
import { KanbanCard } from "../../model/kanban/KanbanCard";
import { Entry } from "../../model/entry/Entry";
import { SpinnerFullScreen } from "../../components/SpinnerFullScreen";
import { Spacing } from "../../theme/Spacing";
import { Breakpoints } from "../../theme/Breakpoints";
import { Rounded } from "../../theme/Rounded";
import { Shadow } from "../../theme/Shadow";
import { AtiraIcon } from "../../components/AtiraIcon";
import { TextWithTruncated } from "../../components/TextWithTruncated";
import { SearchCardTask } from "./components/SearchCardTask";
import { permissionActions } from "../../redux/permission/permission.slice";
import { Info } from "../../components/Info";
import { useLocation, useNavigate } from "react-router-dom";
import { BaseTask } from "../../model/task/Task";
import { AtiraSelect } from "../../components/AtiraSelect";
import camelCase from "lodash/camelCase";
import { SearchKind } from "../../model/search/types/SearchKind.enum";
import { standardDate } from "../../utils/Date";
import { Button } from "../../components/Button";
import { faEye } from "@fortawesome/free-solid-svg-icons/faEye";
import { faCalendar } from "@fortawesome/free-solid-svg-icons/faCalendar";
import { entryActions } from "../../redux/entry/entry.slice";
import { useDrawersContext } from "../tasks/drawers-context";
import { AtiraCollapse } from "../../components/AtiraCollapse";
import { AssigneeBanner } from "../../components/AssigneeBanner";
import { permissionSliceSelectors } from "../../redux/permission/permission.selector";
import { EntityIcons, PageIcons } from "../../utils/GlobalIcons";
import { ModuleDefinition } from "../../model/module/types/module-definition/ModuleDefinition";
import { getFontAwesomeIcon } from "../../utils/getFontAwesomeIcon";
import { BaseModuleInstance } from "../../model/module/types/module-instance/BaseModuleInstance";
import { moduleActions } from "../../redux/module/module.slice";
import { SearchCardModuleInstance } from "./components/SearchCardModuleInstance";

type EntityKind = SearchResponseDto["results"][number]["_kind"];

const getEntityIcon = (kind: EntityKind, parentModule?: ModuleDefinition) => {
  switch (kind) {
    case "deal":
      return EntityIcons.Deals;
    case "task":
      return EntityIcons.Tasks;
    case "entry":
      return EntityIcons.Entries;
    case "module_instance":
      return getFontAwesomeIcon(parentModule?.icon) || EntityIcons.Modules;
    default:
      return EntityIcons.Entries;
  }
};

const getEntityColor = (kind: EntityKind) => {
  switch (kind) {
    case "deal":
      return "junk";
    case "task":
      return "lead";
    case "module_instance":
      return "warning";
    case "entry":
    default:
      return "main";
  }
};

const ResultsWrapper = styled(Flex)`
  flex-direction: column;
  gap: ${Spacing.m};
  width: 100%;
`;

const Card = styled(Flex)`
  flex-direction: column;
  background: ${({ theme }) => theme.white};
  border-radius: ${Rounded.md};
  box-shadow: none;
  transition: all 0.3s;
  border: ${({ theme }) => `1px solid ${theme.darkSub}`};

  .atira-search-text {
    font-size: ${FontSize.s};

    strong,
    svg {
      color: ${({ theme }) => theme.main};
    }

    span {
      color: ${({ theme }) => theme.textColor};
    }

    u {
      text-decoration: none;
      background-color: ${({ theme }) => theme.lightMain}90;
      color: ${({ theme }) => theme.white};
      font-weight: 600;
      padding-inline: 2px;
    }
  }

  &:hover {
    box-shadow: ${Shadow.SM};
  }
`;

const SourceBadge = styled(Flex)<{ kind?: EntityKind }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.xs} ${Spacing.m};
  border-radius: ${Rounded.pill};
  font-size: ${FontSize.s};
  background: ${({ kind, theme }) => theme[getEntityColor(kind || "entry")]};
  color: ${({ theme }) => theme.white};
  min-width: 6rem;
`;

const StyledInput = styled(Input)`
  transition: 0.3s all;
  html[dir="rtl"] & {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }
  html[dir="ltr"] & {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }
`;

const InputContainer = styled(Flex)`
  flex-direction: column;
  gap: ${Spacing.s};

  @media (min-width: ${Breakpoints.DESKTOP}) {
    width: 60%;
    align-self: center;
  }
`;

const CardTitleContainer = styled(Flex)`
  justify-content: space-between;
  align-items: center;
  gap: ${Spacing.m};
  min-width: 0;

  & > :last-child {
    flex-shrink: 0;
  }
`;

const ResultTitle = styled(TextWithTruncated)`
  unicode-bidi: plaintext;
`;

const ResultContainer = styled(Flex)`
  flex-direction: column;
  gap: ${Spacing.m};

  @media (min-width: ${Breakpoints.DESKTOP}) {
    width: 60%;
    align-self: center;
  }
`;

const SearchSelectWrapper = styled(Flex)`
  .ant-select {
    width: 10rem !important;
    border-radius: 0;
    border-inline-start: 0;
    border-start-end-radius: ${Rounded.md};
    border-end-end-radius: ${Rounded.md};
    border-color: ${({ theme }) => theme.darkSub};

    &:focus,
    &:focus-within {
      border-color: ${({ theme }) => theme.darkSub} !important;
      box-shadow: none !important;
    }
  }
`;

const StyledCollapse = styled(AtiraCollapse)`
  border-radius: 0;
`;

const CardFooter = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  gap: ${Spacing.m};
  background-color: ${({ theme }) => theme.darkSub};
  border-radius: 0 0 ${Rounded.md} ${Rounded.md};

  & > div {
    margin-inline-start: ${Spacing.m};
  }

  p {
    font-size: ${FontSize.s};
  }
`;

const PreviewButton = styled(Button)`
  margin: 0;
  border-radius: 0;
  border-end-end-radius: ${Rounded.md};
  padding-inline: ${Spacing.xl};

  &:hover {
    background-color: ${({ theme }) => theme.lightMain};
  }
`;

const Search: React.FC = () => {
  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const loading = useAppSelector(appSliceSelectors.selectSearchLoading);
  const searchResults =
    useAppSelector(appSliceSelectors.selectSearchResults) || [];
  const searchOptions = useAppSelector(appSliceSelectors.selectSearchOptions);
  const user = useAppSelector(userSliceSelectors.selectLoggedInUser)!;
  const members = useAppSelector(permissionSliceSelectors.selectMembers);

  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const {
    setEntry,
    setEntryDetailsVisible,
    setTask,
    setTaskReadVisible,
    setKanbanCard,
    setKanbanCardReadVisible,
    setModuleInstance,
    setModuleInstanceReadVisible,
  } = useDrawersContext();
  const { getValues, setValue, control } = useForm<SearchDto>();

  const keyword = searchOptions?.keyword || "";
  const kind = searchOptions?.kind || SearchKind.ALL;

  const onPreviewEntity = (entity: SearchResponseDto["results"][number]) => {
    if (entity._kind === "entry") {
      setEntry(entity as Entry);
      setEntryDetailsVisible(true);
    } else if (entity._kind === "task") {
      setTask(entity as BaseTask);
      setTaskReadVisible(true);
    } else if (entity._kind === "module_instance") {
      setModuleInstance(entity as BaseModuleInstance);
      setModuleInstanceReadVisible(true);
    } else {
      setKanbanCard(entity as KanbanCard);
      setKanbanCardReadVisible(true);
    }
  };

  const onFetchEntries = useCallback(() => {
    const keyword = getValues("keyword") || "";
    const kind = getValues("kind") || SearchKind.ALL;

    dispatch(
      appActions.search({
        userId,
        keyword,
        kind,
      }),
    );
    dispatch(appActions.setSearchOptions({ keyword, kind }));
  }, [dispatch, userId, getValues]);

  const onEntityKindChange = (value: string) => {
    setValue("kind", value as SearchKind);
    dispatch(
      appActions.setSearchOptions({
        keyword: getValues("keyword") || "",
        kind: value as SearchKind,
      }),
    );
    onFetchEntries();
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(debounce(onFetchEntries, 1000), [
    onFetchEntries,
  ]);

  useEffect(() => {
    dispatch(permissionActions.getMembers({ userId }));
    dispatch(entryActions.getUserTableSettings({ userId }));
    dispatch(moduleActions.getAllModuleDefinitions({ userId }));
  }, [dispatch, userId]);

  useEffect(() => {
    if (keyword?.length || kind) {
      setValue("keyword", keyword);
      setValue("kind", kind);

      onFetchEntries();
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [keyword, kind, setValue, onFetchEntries, navigate, location.pathname]);

  useEffect(() => {
    return () => {
      dispatch(
        appActions.setSearchOptions({
          keyword: "",
          kind: SearchKind.ALL,
        }),
      );
    };
  }, [dispatch]);

  return (
    <Fragment>
      <Flex flexDirection="column">
        <SubHeader title={t("common.search")} icon={PageIcons.Search} />

        <Flex
          paddingInline="m"
          paddingBottom="m"
          flexDirection="column"
          gap="xl"
        >
          <Flex gap="s" flexDirection="column">
            <Text fontSize="l" fontWeight="bold" align="center">
              {t("search.title")}
            </Text>

            <InputContainer>
              <Flex>
                <Controller
                  control={control}
                  name="keyword"
                  render={({ field: { value, onChange } }) => (
                    <StyledInput
                      type="search"
                      placeholder={t("search.input.placeholder")}
                      onChange={(v) => {
                        onChange(v.target.value);
                        debouncedSearch();
                      }}
                      autoFocus
                      value={value}
                    />
                  )}
                />

                <SearchSelectWrapper>
                  <AtiraSelect
                    options={Object.values(SearchKind).map((v) => ({
                      label: t(`common.${camelCase(v)}`),
                      value: v,
                    }))}
                    onChange={onEntityKindChange}
                    value={kind}
                  />
                </SearchSelectWrapper>
              </Flex>

              <Info info={t("search.info")} />
            </InputContainer>
          </Flex>

          {loading ? <SpinnerFullScreen containerHeight={"10rem"} /> : null}

          {!loading && !searchResults?.length && keyword?.length ? (
            <AtiraEmpty description={t("search.results.empty")} />
          ) : null}

          {!loading && searchResults?.length ? (
            <ResultContainer>
              <Text fontSize="l" fontWeight={"bold"}>
                {t("search.results.title", {
                  count: searchResults?.length,
                })}
              </Text>

              <ResultsWrapper>
                {searchResults.map((result) => {
                  const resultWithAnyType = result as any;
                  const title =
                    resultWithAnyType?.title ||
                    resultWithAnyType?.fields?.name ||
                    resultWithAnyType?.fields?.email ||
                    resultWithAnyType?.fields?.phone ||
                    resultWithAnyType?.name ||
                    (result._kind === "entry"
                      ? t("search.entry.unnamed")
                      : t("common.untitled"));

                  const assigneeIds =
                    result._kind === "deal"
                      ? resultWithAnyType?.entryDoc?.assigneeIds
                      : resultWithAnyType?.assigneeIds;

                  return (
                    <Card key={result._id}>
                      <StyledCollapse
                        items={[
                          {
                            key: result._id,
                            label: (
                              <Flex
                                flexDirection="column"
                                gap="xs"
                                width={"100%"}
                              >
                                <CardTitleContainer>
                                  <Flex
                                    alignItems="center"
                                    gap="s"
                                    minWidth={0}
                                  >
                                    <ResultTitle
                                      fitToWidth
                                      fontSize="s"
                                      fontWeight="bold"
                                      color="main"
                                    >
                                      {title}
                                    </ResultTitle>

                                    {assigneeIds?.length ? (
                                      <AssigneeBanner
                                        assignees={members?.filter((m) =>
                                          assigneeIds?.includes(m._id),
                                        )}
                                      />
                                    ) : null}
                                  </Flex>

                                  <SourceBadge
                                    kind={result._kind as EntityKind}
                                  >
                                    <AtiraIcon
                                      icon={getEntityIcon(
                                        result._kind as EntityKind,
                                        (result as Partial<BaseModuleInstance>)
                                          .parent,
                                      )}
                                    />
                                    {t(`common.${camelCase(result._kind)}`)}
                                  </SourceBadge>
                                </CardTitleContainer>
                              </Flex>
                            ),
                            children:
                              result._kind === "entry" ? (
                                <SearchCardEntry entry={result as Entry} />
                              ) : result._kind === "deal" ? (
                                <SearchCardKanbanCard
                                  kanbanCard={result as KanbanCard}
                                />
                              ) : result._kind === "module_instance" ? (
                                <SearchCardModuleInstance
                                  instance={result as BaseModuleInstance}
                                />
                              ) : (
                                <SearchCardTask task={result as BaseTask} />
                              ),
                          },
                        ]}
                      />

                      <CardFooter>
                        <Flex alignItems="center" gap="s">
                          <AtiraIcon icon={faCalendar} />

                          <Text>
                            <strong>{t("common.createdAt")}: </strong>
                            {standardDate<string>(
                              result.createdAt!,
                              user,
                              true,
                            )}
                          </Text>
                        </Flex>

                        <PreviewButton
                          onClick={() => onPreviewEntity(result)}
                          icon={faEye}
                        >
                          {t("common.preview")}
                        </PreviewButton>
                      </CardFooter>
                    </Card>
                  );
                })}
              </ResultsWrapper>
            </ResultContainer>
          ) : null}
        </Flex>
      </Flex>
    </Fragment>
  );
};

export default Search;
